import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useI18n } from '../context/I18nContext';

const Navbar = () => {
  const { cartCount } = useCart();
  const { language, setLanguage, t } = useI18n();
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  const navLinks = [
    { to: '/', label: t('nav.home') },
    { to: '/products', label: t('nav.shop') },
    { to: '/cart', label: t('nav.cart') },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="bg-amber-50 border-b border-amber-200 sticky top-0 z-50 shadow-sm">
      <div className="bg-orange-700 text-orange-50 text-center text-xs sm:text-sm py-2 px-4 font-semibold tracking-wide">
        {t('global.siteInConstruction')}
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2">
            <span className="text-2xl">🐾</span>
            <span className="text-xl font-bold text-amber-800 font-serif">Paws Eternal</span>
          </Link>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-8">
            <label className="flex items-center gap-2 text-sm text-amber-800">
              <span>{t('nav.language')}</span>
              <select
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                className="bg-white border border-amber-300 rounded px-2 py-1 text-amber-900"
                aria-label={t('nav.language')}
              >
                <option value="fr">FR</option>
                <option value="en">EN</option>
                <option value="de">DE</option>
              </select>
            </label>
            {navLinks.map(link => (
              <Link
                key={link.to}
                to={link.to}
                className={`text-sm font-medium transition-colors duration-200 ${
                  isActive(link.to)
                    ? 'text-amber-800 border-b-2 border-amber-700'
                    : 'text-amber-700 hover:text-amber-900'
                }`}
              >
                {link.to === '/cart' ? (
                  <span className="relative">
                    🛒
                    {cartCount > 0 && (
                      <span className="absolute -top-2 -right-3 bg-amber-700 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                        {cartCount > 99 ? '99+' : cartCount}
                      </span>
                    )}
                  </span>
                ) : link.label}
              </Link>
            ))}
          </div>

          {/* Mobile menu button */}
          <div className="flex md:hidden items-center gap-4">
            <Link to="/cart" className="relative text-amber-700">
              🛒
              {cartCount > 0 && (
                <span className="absolute -top-2 -right-3 bg-amber-700 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {cartCount > 99 ? '99+' : cartCount}
                </span>
              )}
            </Link>
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className="text-amber-700 hover:text-amber-900 p-2"
              aria-label={t('nav.toggleMenu')}
            >
              {menuOpen ? '✕' : '☰'}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {menuOpen && (
          <div className="md:hidden pb-4 border-t border-amber-200 mt-2 pt-4 flex flex-col gap-4">
            <label className="px-2 text-sm text-amber-800 flex items-center gap-2">
              <span>{t('nav.language')}</span>
              <select
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                className="bg-white border border-amber-300 rounded px-2 py-1 text-amber-900"
                aria-label={t('nav.language')}
              >
                <option value="fr">FR</option>
                <option value="en">EN</option>
                <option value="de">DE</option>
              </select>
            </label>
            {[{ to: '/', label: t('nav.home') }, { to: '/products', label: t('nav.shop') }].map(link => (
              <Link
                key={link.to}
                to={link.to}
                onClick={() => setMenuOpen(false)}
                className={`text-sm font-medium px-2 ${
                  isActive(link.to) ? 'text-amber-900 font-bold' : 'text-amber-700'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
